import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ClipboardList,
  FileText,
  Loader2,
  Lock,
  MinusCircle,
  PauseCircle,
  UserCheck,
  XCircle,
} from 'lucide-react';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { amsAuditRunReportService } from '../../../services/amsAuditRunReportService';
import type { AuditRunReport, RunReportCheckpoint } from '../../../services/amsAuditRunReportService';
import { BRAND_COLORS } from '../../../shared/constants/brandColors';
import { buildCheckpointSummaryReport, buildNcrReport, collectNcrItems, printReport } from '../utils/amsAuditReports';

interface AmsAuditRunReportDrawerProps {
  auditId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Shown on the generated reports so a printed copy identifies itself. */
  projectName?: string;
  auditLabel?: string;
}

const STATUS_META: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  pass: {
    label: 'Pass',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle2 size={13} />,
  },
  fail: {
    label: 'Fail',
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: <XCircle size={13} />,
  },
  hold: {
    label: 'On hold',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <PauseCircle size={13} />,
  },
  due: {
    label: 'Not done',
    className: 'bg-gray-50 text-gray-500 border-gray-200',
    icon: <MinusCircle size={13} />,
  },
};

function StatusPill({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? STATUS_META['due']!;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${meta.className}`}
    >
      {meta.icon}
      {meta.label}
    </span>
  );
}

function StatTile({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-center">
      <div className={`text-lg font-bold ${tone ?? 'text-gray-900'}`}>{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
    </div>
  );
}

/** Group rows by the equipment they belong to; project-level rows come last. */
function groupCheckpoints(rows: RunReportCheckpoint[]): Array<{ heading: string; rows: RunReportCheckpoint[] }> {
  const map = new Map<string, RunReportCheckpoint[]>();
  const order: string[] = [];
  for (const row of rows) {
    const key = row.equip || '__project__';
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(row);
  }
  // Project-level section renders last.
  order.sort((a, b) => (a === '__project__' ? 1 : b === '__project__' ? -1 : 0));
  return order.map((key) => ({
    heading: key === '__project__' ? 'Project-level checkpoints' : key,
    rows: map.get(key) ?? [],
  }));
}

/**
 * Read-only view of what the mobile field app recorded for an audit: run
 * status, OMS check-in, sign-offs, every checkpoint result with its evidence,
 * and the module findings. The office side of the field-audit loop.
 */
export default function AmsAuditRunReportDrawer({
  auditId,
  isOpen,
  onClose,
  projectName,
  auditLabel,
}: AmsAuditRunReportDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<AuditRunReport | null>(null);

  useEffect(() => {
    if (!isOpen || !auditId) return;
    setLoading(true);
    setError(null);
    amsAuditRunReportService
      .getRunReport(auditId)
      .then(setReport)
      .catch(() => {
        setReport(null);
        setError('Failed to load the run report.');
      })
      .finally(() => setLoading(false));
  }, [isOpen, auditId]);

  const groups = useMemo(() => (report ? groupCheckpoints(report.checkpoints) : []), [report]);
  const locked = report?.audit.runStatus === 'done';
  const ncrCount = useMemo(() => (report ? collectNcrItems(report).length : 0), [report]);

  const generate = (kind: 'summary' | 'ncr') => {
    if (!report) return;
    const meta = { projectName, auditLabel, generatedAt: new Date().toISOString() };
    printReport(kind === 'summary' ? buildCheckpointSummaryReport(report, meta) : buildNcrReport(report, meta));
  };

  return (
    <RightDrawer open={isOpen} onClose={onClose} title="" size="xl">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div
          className="flex-shrink-0 px-6 py-4 border-b border-gray-200"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primaryLight} 0%, ${BRAND_COLORS.primarySoft} 100%)`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center border"
              style={{ backgroundColor: BRAND_COLORS.primary + '18', borderColor: BRAND_COLORS.primaryBorder }}
            >
              <ClipboardList size={24} style={{ color: BRAND_COLORS.primary }} />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Field Run Report</h2>
              <p className="text-sm text-gray-600">
                {locked ? 'Submitted audit — locked, read-only' : 'What the audit team recorded on site'}
              </p>
            </div>
            {report && (
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                  report.audit.runStatus === 'done'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : report.audit.runStatus === 'hold'
                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                }`}
              >
                {report.audit.runStatus === 'done'
                  ? 'Completed & submitted'
                  : report.audit.runStatus === 'hold'
                    ? 'On hold'
                    : 'In progress'}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="text-center py-16 text-red-500">
              <AlertTriangle size={48} className="mx-auto mb-4 opacity-50" />
              <p>{error}</p>
            </div>
          ) : !report ? null : (
            <div className="space-y-6">
              {/* Locked notice + report generation. Reports are built from this
                  submitted run, so they're only offered once it's been submitted. */}
              {locked && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex flex-wrap items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Lock size={14} className="text-slate-500" />
                    Submitted and locked — this record is read-only.
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => generate('summary')}
                      className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      <FileText size={13} /> Checkpoint Summary Report
                    </button>
                    <button
                      type="button"
                      onClick={() => generate('ncr')}
                      className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <AlertTriangle size={13} /> NCR ({ncrCount})
                    </button>
                  </div>
                </div>
              )}

              {/* Progress */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                <StatTile label="Checkpoints" value={report.progress.total} />
                <StatTile label="Done" value={report.progress.done} />
                <StatTile label="Pass" value={report.progress.pass} tone="text-emerald-600" />
                <StatTile label="Fail" value={report.progress.fail} tone="text-red-600" />
                <StatTile label="On hold" value={report.progress.hold} tone="text-amber-600" />
                <StatTile label="Open" value={report.progress.due} tone="text-gray-500" />
              </div>

              {/* Check-in + sign-off */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <UserCheck size={15} className="text-gray-400" />
                  {report.audit.omsChecked ? (
                    <span>
                      OMS/vendor rep checked in
                      {report.audit.omsRep ? (
                        <>
                          {': '}
                          <span className="font-semibold">{report.audit.omsRep}</span>
                        </>
                      ) : null}
                      {report.audit.omsCheckinPhoto && (
                        <a
                          href={report.audit.omsCheckinPhoto}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-2 inline-flex items-center gap-1 text-xs font-medium underline"
                          style={{ color: BRAND_COLORS.primary }}
                        >
                          <Camera size={12} /> photo
                        </a>
                      )}
                    </span>
                  ) : (
                    <span className="text-gray-500">No OMS/vendor check-in recorded yet.</span>
                  )}
                </div>
                <div className="text-sm text-gray-700">
                  Sign-offs:{' '}
                  {report.audit.signedOff.length > 0 ? (
                    <span className="font-semibold">{report.audit.signedOff.join(', ')}</span>
                  ) : (
                    <span className="text-gray-500">none yet</span>
                  )}
                </div>
                {Object.keys(report.audit.assetNos).length > 0 && (
                  <div className="text-sm text-gray-700">
                    Recorded serials:{' '}
                    <span className="text-gray-600">
                      {Object.entries(report.audit.assetNos)
                        .map(([equip, sn]) => `${equip}: ${sn}`)
                        .join(' · ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Checkpoint results, grouped by equipment */}
              {groups.map((group) => (
                <div key={group.heading} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-900">{group.heading}</span>
                    <span className="text-xs text-gray-500">
                      {group.rows.filter((r) => r.status === 'pass' || r.status === 'fail').length}/{group.rows.length}{' '}
                      done
                    </span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {group.rows.map((row, idx) => (
                      <div key={`${row.id}-${row.equipmentId ?? idx}`} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{row.title}</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              {row.type}
                              {row.ideal ? ` · expected: ${row.ideal}${row.unit ? ` ${row.unit}` : ''}` : ''}
                              {row.reading ? ` · reading: ${row.reading}${row.unit ? ` ${row.unit}` : ''}` : ''}
                            </p>
                          </div>
                          <StatusPill status={row.status} />
                        </div>
                        {(row.severity || row.impact || row.observation || row.action) && (
                          <div className="mt-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-0.5">
                            {(row.severity || row.impact) && (
                              <p>
                                {row.severity && (
                                  <span className="font-semibold text-red-700">{row.severity}</span>
                                )}
                                {row.severity && row.impact ? ' · ' : ''}
                                {row.impact && <span>Impact: {row.impact}</span>}
                              </p>
                            )}
                            {row.observation && <p>Observation: {row.observation}</p>}
                            {row.action && <p>Action: {row.action}</p>}
                          </div>
                        )}
                        {row.attachments && row.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {row.attachments.map((url, i) => (
                              <a
                                key={url}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium underline"
                                style={{ color: BRAND_COLORS.primary }}
                              >
                                <Camera size={12} /> evidence {i + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Module findings */}
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <span className="font-semibold text-sm text-gray-900">
                    Module findings ({report.findings.length})
                  </span>
                </div>
                {report.findings.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-gray-500">No module findings logged.</p>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {report.findings.map((f) => (
                      <div key={f.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {f.equip}
                              {f.modules.length > 0 && (
                                <span className="text-gray-500 font-normal"> · {f.modules.join(', ')}</span>
                              )}
                            </p>
                            {(f.severity || f.impact) && (
                              <p className="text-xs text-gray-600 mt-0.5">
                                {f.severity ?? ''}
                                {f.severity && f.impact ? ' · ' : ''}
                                {f.impact ? `Impact: ${f.impact}` : ''}
                              </p>
                            )}
                            {f.observation && <p className="text-xs text-gray-700 mt-1">{f.observation}</p>}
                            {f.action && <p className="text-xs text-gray-700">Action: {f.action}</p>}
                          </div>
                          <StatusPill status={f.pass ? 'pass' : 'fail'} />
                        </div>
                        {f.photos.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {f.photos.map((url, i) => (
                              <a
                                key={url}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium underline"
                                style={{ color: BRAND_COLORS.primary }}
                              >
                                <Camera size={12} /> photo {i + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </RightDrawer>
  );
}
